import React, { useState, useEffect } from 'react';
import { Send } from 'lucide-react';
import { useDesignColors } from '../../hooks/useDesignColors';
import { useThemeSettings } from '../../hooks/useThemeSettings';

interface OrderTrackingInputProps {
  messageId: string;
  onSubmit: (orderNumber: string) => void;
}

export function OrderTrackingInput({ messageId, onSubmit }: OrderTrackingInputProps) {
  const [orderNumber, setOrderNumber] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const { colors } = useDesignColors();
  const { theme } = useThemeSettings();
  const isDark = theme.mode === 'dark';

  // Add animation effect when component mounts
  useEffect(() => {
    // Delay the appearance to allow the message to finish typing
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 300);
    
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (orderNumber.trim()) {
      onSubmit(orderNumber);
      setOrderNumber('');
    }
  };

  return (
    <div 
      className={`ml-8 mt-2 mb-4 transition-all duration-500 ${
        isVisible 
          ? 'opacity-100 translate-y-0' 
          : 'opacity-0 translate-y-4 pointer-events-none'
      }`}
    >
      <form onSubmit={handleSubmit} className="flex space-x-2 max-w-md">
        <input
          type="text"
          value={orderNumber}
          onChange={(e) => setOrderNumber(e.target.value)}
          placeholder="Enter your order number"
          className={`flex-1 h-10 rounded-lg px-4 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-transparent ${
            isDark 
              ? 'bg-gray-700/50 border border-gray-600 text-white placeholder-gray-400' 
              : 'bg-gray-300 border border-gray-300 text-gray-700 placeholder-gray-500'
          }`}
          autoFocus
        />
        <button
          type="submit"
          disabled={!orderNumber.trim()}
          style={{
            background: `linear-gradient(315deg, ${colors.gradientEnd}, ${colors.gradientStart})`,
            opacity: !orderNumber.trim() ? 0.5 : 0.9,
          }}
          className="h-10 px-4 rounded-lg text-white transition-all hover:opacity-100 flex items-center justify-center disabled:cursor-not-allowed"
        >
          <Send className="w-5 h-5" />
        </button>
      </form>
    </div>
  );
}